/**
 * Runtime theming.
 *
 * The theme document from Cosmos is written into CSS custom properties on
 * `<html>`. Tailwind's `@theme` block declares the same variable names, so
 * every utility class in the app (`bg-surface`, `text-ink`, `border-border`)
 * follows whatever Gillian sets in the admin theme editor — no rebuild, no
 * deploy, and no second source of truth for colour.
 */

import type { ThemeSettings } from '@gills/shared';

/** Map a theme document onto the CSS variables declared in styles.css. */
export function applyTheme(theme: ThemeSettings | null | undefined): void {
  if (!theme) return;
  const root = document.documentElement;

  const colors: Record<string, string> = {
    '--color-background': theme.colors.background,
    '--color-surface': theme.colors.surface,
    '--color-ink': theme.colors.ink,
    '--color-muted': theme.colors.muted,
    '--color-primary': theme.colors.primary,
    '--color-primary-ink': theme.colors.primaryInk,
    '--color-secondary': theme.colors.secondary,
    '--color-secondary-ink': theme.colors.secondaryInk,
    '--color-accent': theme.colors.accent,
    '--color-accent-ink': theme.colors.accentInk,
    '--color-border': theme.colors.border,
    '--color-link': theme.colors.link,
    '--color-sale': theme.colors.sale,
    '--color-error': theme.colors.error,
    '--color-success': theme.colors.success,
  };

  for (const [name, value] of Object.entries(colors)) {
    if (value) root.style.setProperty(name, value);
  }

  root.style.setProperty('--font-heading', theme.fonts.headingFamily);
  root.style.setProperty('--font-body', theme.fonts.bodyFamily);
  root.style.setProperty('--font-heading-weight', String(theme.fonts.headingWeight));
  root.style.setProperty('--font-body-weight', String(theme.fonts.bodyWeight));
  // Type scale multipliers, applied to heading sizes via calc().
  root.style.setProperty('--scale-heading', String(theme.fonts.headingScale));
  root.style.setProperty('--scale-body', String(theme.fonts.bodyScale));

  root.style.setProperty('--radius-theme', `${theme.layout.radius}px`);
  root.style.setProperty('--radius-button', `${theme.layout.buttonRadius}px`);
  root.style.setProperty('--container-page', `${theme.layout.containerMaxWidth}px`);
}

/**
 * Cache the theme in localStorage and re-apply it before React mounts.
 *
 * Without this the first paint uses the CSS fallbacks and then snaps to the
 * real theme once /api/storefront resolves — a visible flash on every cold
 * load. Reading a cached copy synchronously avoids it.
 */
const CACHE_KEY = 'gills.theme.v1';

export function cacheTheme(theme: ThemeSettings): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(theme));
  } catch {
    // Private browsing or a full quota — the theme still applies this session.
  }
}

export function applyCachedTheme(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) applyTheme(JSON.parse(raw) as ThemeSettings);
  } catch {
    // A corrupt cache must never stop the app booting.
  }
}
